<template>
  <div class="w-full mx-auto h-full flex flex-col bg-[#f7f7f7] border-gray-200 fadeIn">
    <div class="flex-1 min-h-0">
      <div
        class="max-w-4xl mx-auto min-h-full bg-white shadow-sm w-full rounded-lg border-[1px] border-gray-200"
      >
        <div class="p-6">
          <!-- Header -->
          <div class="flex items-center w-full mb-2">
            <h2 class="text-xl font-semibold ml-2 flex items-center gap-2">
              <BadgePlus class="enter-spin" /> New job application
            </h2>
          </div>

          <p class="text-gray-500 text-sm mt-0 mb-4">
            Add a link to the job position and we'll fetch the details to auto-fill the form for
            you.
          </p>

          <Form v-model="form" @submit="handleSubmit" class="space-y-4">
            <!-- Role URL -->
            <FormField name="url">
              <FormItem>
                <FormLabel class="flex items-center">
                  <Link class="text-sm h-4 w-4 mr-2" /> Posting URL
                  <span class="text-red-500 ml-0.5">*</span>
                  <span v-if="formErrors.url" class="text-red-500 text-xs ml-auto">{{
                    formErrors.url?.[0]
                  }}</span>
                </FormLabel>
                <FormControl>
                  <div class="flex gap-2 items-center">
                    <Input
                      v-model="form.url"
                      placeholder="https://..."
                      type="url"
                      required
                      :disabled="isAutofillingFromUrl"
                      :class="{ 'border-red-500 focus-visible:ring-red-500': formErrors.url }"
                      @keydown.enter.prevent="handleUrlAutofill"
                    />
                    <Button
                      type="button"
                      class="shrink-0 h-full bg-cosmicPurple text-white hover:bg-cosmicPurple/80 hover:translate-y-[-1px] transition-all duration-200 font-normal"
                      :disabled="isAutofillingFromUrl || !form.url || form.url.trim().length < 10"
                      @click="handleUrlAutofill"
                    >
                      <Loader2 v-if="isAutofillingFromUrl" class="w-4 h-4 animate-spin" />
                      <Sparkles v-else class="w-4 h-4" />
                      <span class="ml-1.5">{{
                        isAutofillingFromUrl ? 'Fetching…' : 'Autofill'
                      }}</span>
                    </Button>
                  </div>
                </FormControl>
                <FormDescription class="text-gray-400 text-xs">
                  Paste the job posting URL and click Autofill to prefill the form.
                  <button
                    v-if="!autofillSuccess && !manualFillEnabled"
                    type="button"
                    class="underline hover:text-gray-600 transition-colors focus:outline-none"
                    @click="manualFillEnabled = true"
                  >
                    Or fill manually
                  </button>
                </FormDescription>
              </FormItem>
            </FormField>

            <!-- Autofill loading -->
            <div
              v-if="isAutofillingFromUrl"
              class="flex flex-col items-center justify-center py-10 gap-3 text-gray-500"
            >
              <Loader2 class="w-8 h-8 animate-spin text-cosmicPurple" />
              <p class="text-sm font-medium">Getting job details&hellip;</p>
              <p class="text-xs text-gray-400">Please wait while we process the job posting page</p>
            </div>

            <!-- Scrape-failed error with manual fill option -->
            <div
              v-if="!isAutofillingFromUrl && scrapeFailedMessage && !manualFillEnabled"
              class="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 fadeIn"
            >
              <AlertCircle class="w-4 h-4 mt-0.5 shrink-0 text-red-500" />
              <div>
                <p class="font-medium">{{ scrapeFailedMessage }}</p>
                <p class="text-red-700 font-normal mt-0.5">
                  The URL might be incorrect or the position has closed. Alternatively, you can
                  <button
                    type="button"
                    class="underline font-medium text-red-800 hover:text-red-900 focus:outline-none"
                    @click="manualFillEnabled = true; scrapeFailedMessage = null"
                  >
                    fill the form manually</button
                  >.
                </p>
              </div>
            </div>

            <!-- Fields: revealed after autofill or manual fill -->
            <template v-if="autofillSuccess || manualFillEnabled">
              <!-- Review / manual fill banner -->
              <div
                class="flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-800 fadeIn"
              >
                <ShieldCheck class="w-4 h-4 mt-0.5 shrink-0 text-gray-600" />
                <div v-if="autofillSuccess && jobAlreadyInBoard">
                  <p class="font-medium">This job is already listed in Tracktalent.</p>
                  <p class="text-gray-700 font-normal mt-0.5">
                    Details have been pre-filled from our database. You can track this as your own
                    private application.
                  </p>
                </div>
                <div v-else-if="autofillSuccess">
                  <p class="font-medium">Please review the auto-filled data before submitting.</p>
                  <p class="text-gray-700 font-normal mt-0.5">
                    AI extraction may occasionally miss or misread details. Please double-check the
                    filled fields for accuracy.
                  </p>
                </div>
                <div v-else>
                  <p class="font-medium">Fill in the job posting details manually.</p>
                  <p class="text-gray-700 font-normal mt-0.5">
                    Enter the job information below and submit when ready.
                  </p>
                </div>
              </div>

              <!-- Role Name -->
              <FormField name="role_name">
                <FormItem>
                  <FormLabel class="flex items-center">
                    <Laptop class="text-sm h-4 w-4 mr-2" />Job title
                    <span class="text-red-500 ml-0.5">*</span>
                    <span v-if="formErrors.role_name" class="text-red-500 text-xs ml-auto">{{
                      formErrors.role_name?.[0]
                    }}</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      v-model="form.role_name"
                      placeholder="e.g. Senior Software Engineer"
                      required
                      :class="{ 'border-red-500 focus-visible:ring-red-500': formErrors.role_name }"
                    />
                  </FormControl>
                </FormItem>
              </FormField>

              <!-- Company -->
              <EmployerPicker
                v-model="form.employer"
                :initial-employer="autofillEmployer"
                @error="(message) => (formErrors.employer = [message])"
              />
              <div v-if="formErrors.employer" class="text-red-500 text-sm -mt-2">
                {{ formErrors.employer[0] }}
              </div>

              <!-- Seniority Level -->
              <FormField name="seniority_level">
                <FormItem>
                  <FormLabel class="flex items-center">
                    <ChartColumnBig class="text-sm h-4 w-4 mr-2" />Seniority Level
                    <span v-if="formErrors.seniority_level" class="text-red-500 text-xs ml-auto">{{
                      formErrors.seniority_level?.[0]
                    }}</span>
                  </FormLabel>
                  <FormControl>
                    <Select v-model="form.seniority_level">
                      <SelectTrigger
                        :class="{
                          'border-red-500 focus-visible:ring-red-500': formErrors.seniority_level
                        }"
                      >
                        <SelectValue placeholder="Select seniority level" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectItem
                            v-for="level in experienceLevels"
                            :key="level.id"
                            :value="level.id"
                          >
                            {{ level.label }}
                          </SelectItem>
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </FormControl>
                </FormItem>
              </FormField>

              <!-- Authorization Requirements -->
              <FormField name="work_authorization_requirement">
                <FormItem>
                  <FormLabel class="flex items-center">
                    <EarthLock class="text-sm h-4 w-4 mr-2" />Local work authorization
                  </FormLabel>
                  <FormControl>
                    <Select v-model="form.work_authorization_requirement">
                      <SelectTrigger>
                        <SelectValue placeholder="e.g. Visa required, etc." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectItem
                            v-for="req in authorizationRequirements"
                            :key="req.id"
                            :value="req.id"
                          >
                            {{ req.label }}
                          </SelectItem>
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </FormControl>
                </FormItem>
              </FormField>

              <!-- Locations -->
              <SmartLocationPicker v-model="selectedLocations" />

              <!-- Remote Position -->
              <FormField name="is_remote">
                <FormItem>
                  <div class="flex items-center space-x-2">
                    <FormControl>
                      <Checkbox v-model="form.is_remote" id="is_remote" />
                    </FormControl>
                    <FormLabel
                      for="is_remote"
                      class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                    >
                      This is a remote position
                    </FormLabel>
                  </div>
                </FormItem>
              </FormField>

              <hr class="my-2" />
              <p class="text-sm font-medium">Optional additional information</p>
              <p class="text-gray-500 text-xs">
                This helps you keep track of the role details alongside your application.
              </p>

              <!-- Description -->
              <FormField name="description">
                <FormItem>
                  <FormLabel class="flex items-center">
                    <FileText class="text-sm h-4 w-4 mr-2" />Description
                    <span v-if="formErrors.description" class="text-red-500 text-xs ml-auto">{{
                      formErrors.description?.[0]
                    }}</span>
                  </FormLabel>
                  <FormDescription class="text-gray-500 text-sm">
                    What does this role do? Key responsibilities and day-to-day tasks.
                  </FormDescription>
                  <FormControl>
                    <Textarea
                      v-model="form.description"
                      placeholder="Describe the role..."
                      :class="{
                        'border-red-500 focus-visible:ring-red-500': formErrors.description
                      }"
                    />
                  </FormControl>
                </FormItem>
              </FormField>

              <!-- Qualifications -->
              <FormField name="qualifications">
                <FormItem>
                  <FormLabel class="flex items-center">
                    <ListChecks class="text-sm h-4 w-4 mr-2" />Qualifications
                    <span v-if="formErrors.qualifications" class="text-red-500 text-xs ml-auto">{{
                      formErrors.qualifications?.[0]
                    }}</span>
                  </FormLabel>
                  <FormDescription class="text-gray-500 text-sm">
                    Required skills, experience, and education needed for this role.
                  </FormDescription>
                  <FormControl>
                    <Textarea
                      v-model="form.qualifications"
                      placeholder="List required skills, experience, and education..."
                      :class="{
                        'border-red-500 focus-visible:ring-red-500': formErrors.qualifications
                      }"
                    />
                  </FormControl>
                </FormItem>
              </FormField>
            </template>
          </Form>

          <!-- Submit button -->
          <div
            v-if="autofillSuccess || manualFillEnabled"
            class="flex items-center justify-end gap-2 mt-6"
          >
            <router-link
              to="/applications"
              class="bg-gray-200 text-black px-4 py-2 rounded-md hover:bg-gray-300 transition-all duration-200 text-xs font-normal h-8 flex items-center"
            >
              Cancel
            </router-link>
            <Button
              @click="handleSubmit"
              :disabled="isLoading"
              class="bg-cosmicPurple text-white px-4 py-2 rounded-md hover:bg-cosmicPurple/80 transition-all duration-200 hover:shadow-md hover:translate-y-[-1px] text-xs font-normal h-8 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ buttonText }}
            </Button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import {
  Link,
  Laptop,
  ChartColumnBig,
  EarthLock,
  BadgePlus,
  FileText,
  ListChecks,
  Loader2,
  ShieldCheck,
  AlertCircle,
  Sparkles
} from 'lucide-vue-next'
import { useEmployerStore } from '@/stores/employer'
import { useUserStore } from '@/stores/user'
import { useRolesStore } from '@/stores/roles'
import experienceLevels from '@/const/experienceLevels'
import authorizationRequirements from '@/const/authorizationRequirements'
import type { AllowedLocation } from '@/types/locations'
import { LocationTypeEnum } from '@/types/locations'
import { useToast } from '@/components/ui/toast'
import { autofillFromUrl } from '@/utils/autofillFromUrl'
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import SmartLocationPicker from '@/components/common/SmartLocationPicker.vue'
import EmployerPicker from '@/components/common/EmployerPicker.vue'
import { useApplicationsStore } from '@/stores/applications'

const router = useRouter()
const rolesStore = useRolesStore()
const employerStore = useEmployerStore()
const userStore = useUserStore()
const applicationsStore = useApplicationsStore()
const isLoading = ref(false)
const { toast } = useToast()

// Autofill state
const isAutofillingFromUrl = ref(false)
const autofillSuccess = ref(false)
const manualFillEnabled = ref(false)
const scrapeFailedMessage = ref<string | null>(null)
const jobAlreadyInBoard = ref(false)

// Employer hint set by autofill so EmployerPicker can display it even if it
// isn't in the currently-loaded paginated options list.
const autofillEmployer = ref<{ id: number; name: string; logo: string | null } | null>(null)

const selectedLocations = ref<AllowedLocation[]>([])

const form = ref({
  role_name: '',
  employer: null as number | null,
  seniority_level: null as string | null,
  url: '',
  work_authorization_requirement: null as string | null,
  is_remote: false,
  qualifications: '',
  description: ''
})

const formErrors = ref<Record<string, string[]>>({})

const formattedLocations = computed(() => selectedLocations.value.map((loc) => loc.id))

const buttonText = computed(() => (isLoading.value ? 'Submitting…' : 'Submit'))

// When the URL is edited, collapse back to URL-only state
watch(
  () => form.value.url,
  () => {
    autofillSuccess.value = false
    scrapeFailedMessage.value = null
    manualFillEnabled.value = false
    autofillEmployer.value = null
    jobAlreadyInBoard.value = false
    formErrors.value = {}
  }
)

onMounted(async () => {
  await Promise.all([employerStore.fetchInitialData(), userStore.fetchCurrentUser()])
})

// eslint-disable-next-line complexity
async function handleUrlAutofill() {
  if (!form.value.url || form.value.url.trim().length < 10) return

  isAutofillingFromUrl.value = true
  scrapeFailedMessage.value = null
  jobAlreadyInBoard.value = false

  try {
    const result = await autofillFromUrl(form.value.url)

    if (result.hasError) {
      // If the URL already exists as a public job, fetch that role and pre-fill from it.
      // For a private application this is fine — the user is just tracking their own attempt.
      if (result.errorType === 'not_unique' && result.roleId) {
        jobAlreadyInBoard.value = true
        try {
          const role = await rolesStore.fetchRoleById(String(result.roleId))
          if (role) {
            populateFromRole(role)
            autofillSuccess.value = true
          } else {
            manualFillEnabled.value = true
          }
        } catch {
          manualFillEnabled.value = true
        }
        return
      }

      if (result.errorType === 'scrape_failed') {
        scrapeFailedMessage.value = result.errorMessage
        delete formErrors.value.url
      } else {
        formErrors.value.url = [result.errorMessage || 'Invalid URL']
      }
      return
    }

    const autofillData = result.autofillData
    if (!autofillData) return

    autofillSuccess.value = true

    form.value.role_name = autofillData.role_name || form.value.role_name
    form.value.employer = autofillData.employer
    if (autofillData.employer && autofillData.employer_details?.name) {
      const hint = {
        id: autofillData.employer,
        name: autofillData.employer_details.name,
        logo: autofillData.employer_details.logo || null
      }
      autofillEmployer.value = hint
      employerStore.ensureEmployerInOptions({
        ...hint,
        url: autofillData.employer_details.url || null
      })
    }
    form.value.seniority_level = autofillData.seniority_level as any
    form.value.is_remote = autofillData.is_remote
    form.value.description = autofillData.description || ''
    form.value.qualifications = autofillData.qualifications || ''

    // Merge matched + newly-created locations from suggestions
    const matchedLocations: AllowedLocation[] = (autofillData.locations_details || []).map(
      (loc: any) => ({
        id: Number(loc.id),
        city: loc.city ?? null,
        city_details: loc.city_details ?? null,
        country: loc.country ?? null,
        country_details: loc.country_details ?? null,
        location_type: loc.location_type as LocationTypeEnum,
        region: loc.region ?? null,
        us_state: loc.us_state ?? null,
        location_name: loc.location_name || ''
      })
    )

    const createdLocations: AllowedLocation[] = []
    for (const suggestion of autofillData.suggested_locations || []) {
      try {
        const { default: api } = await import('@/utils/api')
        const { API_ROUTES } = await import('@/const/apiRoutes')
        const res = await api.post(API_ROUTES.CREATE_JOB_LOCATION, suggestion.create_data || {})
        createdLocations.push(res.data as AllowedLocation)
      } catch {
        // Non-fatal: skip unresolved location suggestions
      }
    }

    selectedLocations.value = [...matchedLocations, ...createdLocations].filter(
      (loc, idx, arr) => arr.findIndex((l) => l.id === loc.id) === idx
    )
  } finally {
    isAutofillingFromUrl.value = false
  }
}

function populateFromRole(role: any) {
  form.value.role_name = role.role_name || ''
  form.value.employer =
    typeof role.employer === 'number' ? role.employer : (role.employer_details?.id ?? null)
  form.value.seniority_level = role.seniority_level || null
  form.value.work_authorization_requirement = role.work_authorization_requirement || null
  form.value.is_remote = role.is_remote || false
  form.value.description = role.description || ''
  form.value.qualifications = role.qualifications || ''

  if (form.value.employer && role.employer_details?.name) {
    const hint = {
      id: form.value.employer,
      name: role.employer_details.name,
      logo: role.employer_details.logo || null
    }
    autofillEmployer.value = hint
    employerStore.ensureEmployerInOptions({ ...hint, url: role.employer_details.url || null })
  }

  if (role.locations_details?.length) {
    selectedLocations.value = role.locations_details.map((loc: any) => ({
      id: Number(loc.id),
      city: null,
      city_details: null,
      country: null,
      country_details: null,
      location_type: LocationTypeEnum.CITY as any,
      region: null,
      us_state: null,
      location_name: String(loc.location_name || '')
    }))
  }
}

async function handleSubmit() {
  try {
    isLoading.value = true
    formErrors.value = {}

    // Create a new employer record if the user typed a free-text name
    if (typeof form.value.employer === 'string') {
      const newEmployer = await employerStore.checkAndCreateEmployer(form.value.employer as any)
      form.value.employer = newEmployer.id
    }

    const applicationData = {
      role_name: form.value.role_name,
      url: form.value.url,
      seniority_level: form.value.seniority_level || 'UNSPECIFIED',
      employer_id: form.value.employer || 0,
      work_authorization_requirement: form.value.work_authorization_requirement || 'UNDISCLOSED',
      qualifications: form.value.qualifications,
      description: form.value.description,
      is_remote: form.value.is_remote,
      locations: formattedLocations.value,
      ...(selectedLocations.value.length > 0 && {
        country_id: selectedLocations.value[0].country ?? undefined,
        city_id: selectedLocations.value[0].city ?? undefined,
        region: selectedLocations.value[0].region ?? undefined
      })
    }

    const response = await applicationsStore.createPrivateApplication(applicationData)

    toast({
      title: 'Success',
      description: 'Job application created successfully!'
    })

    setTimeout(() => {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      router.push(`/applications/${response.id}`)
    }, 500)
  } catch (error: any) {
    console.error('Failed to create private application:', error)

    if (error && typeof error === 'object' && !Array.isArray(error)) {
      formErrors.value = error
      toast({
        title: 'Validation Error',
        description: 'Please check the form for errors',
        variant: 'destructive'
      })
    } else {
      toast({
        title: 'Error',
        description: 'Failed to submit job application',
        variant: 'destructive'
      })
    }
  } finally {
    isLoading.value = false
  }
}
</script>

<style scoped>
.transition-opacity {
  transition-property: opacity;
}
</style>
